import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "../../components/ui/pagination";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { cn } from "../../lib/utils";
import { ChevronLeft, ChevronRight, Eye, ExternalLink, Search } from "lucide-react";
import { formatCurrencyINR } from "../../lib/enhanced-currency-utils";
import { PurchaseOrder } from "../../types/purchaseOrder";

interface InvoiceTableProps {
  purchaseOrders: PurchaseOrder[];
  onViewDetails: (invoice: any) => void;
  pagination?: {
    totalRecords: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
  };
  onPageChange?: (page: number, pageSize: number) => void;
  loading?: boolean;
  invoices?: any[];
}

const statusStyles: Record<string, string> = {
  paid: "bg-green-100 text-green-700 border-green-200 hover:bg-green-700 hover:text-white",
  pending: "bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-700 hover:text-white",
  overdue: "bg-red-100 text-red-700 border-red-200 hover:bg-red-700 hover:text-white",
};

/** Display as dd/mm/yyyy (handles YYYY-MM-DD without UTC day-shift). */
const formatDateDdMmYyyy = (value: string | null | undefined): string => {
  if (value == null || value === "") return "-";
  const trimmed = String(value).trim();

  const isoDate = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;
  }

  const dmy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    return `${dmy[1].padStart(2, "0")}/${dmy[2].padStart(2, "0")}/${dmy[3]}`;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return trimmed;
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${day}/${month}/${year}`;
};

export const InvoiceTable = ({
  onViewDetails,
  pagination,
  onPageChange,
  loading = false,
  invoices = [],
}: InvoiceTableProps) => {
  const totalPages = pagination?.totalPages ?? 1;

  return (
    <div className="overflow-hidden rounded-[20px] border border-slate-200 bg-white">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-slate-200 bg-slate-50/70 hover:bg-slate-50/70">
              <TableHead className="py-4 pl-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Invoice Number
              </TableHead>
              <TableHead className="w-[100px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                PO ID
              </TableHead>
              <TableHead className="w-[100px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Supplier
              </TableHead>
              <TableHead className="w-[160px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Customer
              </TableHead>
              <TableHead className="w-[160px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Invoice Amount
              </TableHead>
              <TableHead className="w-[120px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Paid Amount
              </TableHead>
              <TableHead className="w-[120px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Balance Due
              </TableHead>
              <TableHead className="w-[120px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Issued Date
              </TableHead>
              <TableHead className="w-[120px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Due Date
              </TableHead>
              <TableHead className="w-[100px] px-2 text-center align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Status
              </TableHead>
              <TableHead className="pr-5 text-center text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} className="py-16">
                  <div className="flex items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-[#3f8b98]" />
                  </div>
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="py-20">
                  <div className="flex flex-col items-center justify-center text-slate-400">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
                      <Search className="h-8 w-8 opacity-20" />
                    </div>
                    <p className="text-sm font-bold uppercase tracking-wider text-slate-500">
                      No invoices found
                    </p>
                    <p className="mt-1 text-[11px]">Try adjusting your search query</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow
                  key={invoice.id}
                  className="border-b border-slate-100 hover:bg-slate-50/40"
                >
                  <TableCell className="py-3.5 pl-5 text-[13px] font-semibold text-slate-800">
                    {invoice.number}
                  </TableCell>
                  <TableCell className="w-[100px] px-2 text-left align-middle">
                    <button
                      onClick={() => onViewDetails(invoice)}
                      className="inline-flex items-center gap-2 text-left text-[13px] font-semibold text-[#2f93b4] transition-colors hover:text-[#25788f]"
                    >
                      {invoice.purchase_order_code}
                      <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  </TableCell>
                  <TableCell className="w-[100px] px-2 text-left align-middle text-[13px] font-medium text-slate-700">
                    {invoice.supplierTradeName || invoice.supplierName}
                  </TableCell>
                  <TableCell className="w-[160px] px-2 text-left align-middle text-[13px] font-medium text-slate-700">
                    {invoice.customerBrandName || invoice.customerName}
                  </TableCell>
                  <TableCell className="w-[120px] px-2 text-left align-middle text-[13px] font-semibold text-slate-800">
                    {formatCurrencyINR(invoice.invoiceAmount)}
                  </TableCell>
                  <TableCell className="w-[120px] px-2 text-left align-middle text-[13px] font-medium text-slate-700">
                    {formatCurrencyINR(invoice.paidAmount)}
                  </TableCell>
                  <TableCell className="w-[120px] px-2 text-left align-middle text-[13px] font-medium text-red-500">
                    {formatCurrencyINR(invoice.invoiceAmount - invoice.paidAmount)}
                  </TableCell>
                  <TableCell className="w-[120px] px-2 text-left align-middle text-[13px] font-medium text-slate-500">
                    {formatDateDdMmYyyy(invoice.date)}
                  </TableCell>
                  <TableCell className="w-[120px] px-2 text-left align-middle text-[13px] font-medium text-slate-600">
                    {formatDateDdMmYyyy(invoice.due_date)}
                  </TableCell>
                  <TableCell className="w-[100px] px-2 text-center align-middle">
                    <Badge
                      className={cn(
                        "rounded-full border px-3 py-1 text-[11px] font-semibold shadow-none transition-colors duration-200",
                        statusStyles[invoice.status.toLowerCase()] || statusStyles.pending
                      )}
                    >
                      {invoice.status.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="w-[120px] pr-5 text-right align-middle">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewDetails(invoice)}
                      className="h-10 rounded-xl border-slate-200 bg-white px-4 text-[13px] font-medium text-slate-700 shadow-none transition-colors hover:bg-slate-50"
                    >
                      <Eye className="mr-2 h-4 w-4 text-slate-400" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && totalPages > 1 && (
        <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4">
          <div className="text-[12px] font-medium text-slate-500 sm:text-[13px]">
            Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to{" "}
            {Math.min(
              pagination.currentPage * pagination.pageSize,
              pagination.totalRecords
            )}{" "}
            of {pagination.totalRecords} entries
          </div>

          <Pagination className="mx-0 w-auto justify-end">
            <PaginationContent className="gap-1.5">
              <PaginationItem>
                <button
                  type="button"
                  onClick={() =>
                    onPageChange?.(Math.max(1, pagination.currentPage - 1), pagination.pageSize)
                  }
                  className={cn(
                    "inline-flex h-8 items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900",
                    pagination.currentPage === 1 && "pointer-events-none opacity-50"
                  )}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Prev
                </button>
              </PaginationItem>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (pagination.currentPage <= 3) pageNum = i + 1;
                else if (pagination.currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = pagination.currentPage - 2 + i;
                }

                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      onClick={() => onPageChange?.(pageNum, pagination.pageSize)}
                      isActive={pagination.currentPage === pageNum}
                      className={cn(
                        "flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border p-0 text-[13px] font-bold transition-colors",
                        pagination.currentPage === pageNum
                          ? "border-[#3f8b98] bg-[#3f8b98] text-white hover:bg-[#3f8b98] hover:text-white"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      )}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              <PaginationItem>
                <button
                  type="button"
                  onClick={() =>
                    onPageChange?.(Math.min(totalPages, pagination.currentPage + 1), pagination.pageSize)
                  }
                  className={cn(
                    "inline-flex h-8 items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900",
                    pagination.currentPage === totalPages && "pointer-events-none opacity-50"
                  )}
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};
